import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import PublicShell from '../components/PublicShell.jsx';
import Turnstile from '../components/Turnstile.jsx';

/**
 * Self-service demo access: address, code, in.
 *
 * Public and unauthenticated — it is the one page a prospect reaches without
 * an account, so it carries the brand and stays out of the portal shell.
 *
 * The server answers identically whether or not an address has demoed before,
 * so nothing here should imply otherwise: no "we don't recognise that email",
 * no different wording for a repeat visitor.
 */
export default function Demo() {
  const [stage, setStage] = useState('email');   // email | code
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [minutes, setMinutes] = useState(10);

  /* Bot protection. The server refuses this endpoint without a solved token,
     so the page needs the site key before it can offer the form at all — see
     turnstileService.js for why the whole thing fails closed. */
  const [siteKey, setSiteKey] = useState(undefined);   // undefined = still asking
  const [token, setToken] = useState(null);
  const [resetWidget, setResetWidget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/demo/config')
      .then(r => { if (!cancelled) setSiteKey(r?.siteKey ?? null); })
      .catch(() => { if (!cancelled) setSiteKey(null); });
    return () => { cancelled = true; };
  }, []);

  const request = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api.post('/auth/demo/request', { email, name, turnstileToken: token });
      setMinutes(r?.expiresInMinutes ?? 10);
      setStage('code');
    } catch (err) {
      setError(err.body?.error ?? 'Could not send the code. Try again.');
      /* A Turnstile token is single-use. Without this the visitor's second
         attempt sends one Cloudflare has already retired and fails the same
         way, which reads as a broken site rather than "try again". */
      setToken(null);
      resetWidget?.();
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post('/auth/demo/verify', { email, code });
      // Full reload rather than a route change: the session is new, and every
      // cached query from before it belongs to nobody.
      window.location.href = '/';
    } catch (err) {
      setError(err.body?.error ?? 'That code is not valid. Request a new one.');
      setBusy(false);
    }
  };

  const onEmailStage = stage === 'email';

  return (
    <PublicShell
      eyebrow="Live demo"
      kicker="No account needed"
      heading={onEmailStage ? 'See the portal with live dashboards' : 'Check your inbox'}
      lead={onEmailStage
        ? 'Tell us who you are and we will send you a code. Nothing to install, and nothing to cancel afterwards.'
        : `We sent a six-digit code to ${email}. It expires in ${minutes} minutes and can be used once.`}
      footnote={
        <>
          The demo shows sample data.{' '}
          <a href="https://vizora.com/book-a-free-demo/">Book time with the team</a>{' '}
          to see it with your own.
        </>
      }
    >
      {error && <p className="public-error" role="alert">{error}</p>}

      {onEmailStage ? (
        <form onSubmit={request} className="public-form">
          {/* Not required. Somebody who would rather not say still gets the
              demo - they are simply greeted without a name. Refusing entry
              over it would cost a prospect to gain a field nobody verifies. */}
          <label className="visually-hidden" htmlFor="demo-name">Your name</label>
          <input id="demo-name" className="form-control" type="text"
                 autoComplete="name" placeholder="Your name" maxLength={60}
                 value={name} disabled={busy} autoFocus
                 onChange={e => setName(e.target.value)} />

          <label className="visually-hidden" htmlFor="demo-email">Email address</label>
          <input id="demo-email" className="form-control" type="email" required
                 autoComplete="email" placeholder="name@company.com"
                 value={email} disabled={busy}
                 onChange={e => setEmail(e.target.value)} />

          {siteKey && (
            <Turnstile
              siteKey={siteKey}
              onToken={setToken}
              onReady={fn => setResetWidget(() => fn)}
              onError={() => setError(
                'The verification step could not load. Check that nothing is '
                + 'blocking challenges.cloudflare.com, then reload the page.')}
            />
          )}

          {siteKey === null && (
            <p className="public-error" role="alert">
              The demo is temporarily unavailable. Please try again shortly.
            </p>
          )}

          <button className="btn btn-primary" type="submit"
                  disabled={busy || !email.trim() || !token}>
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
        </form>
      ) : (
        <>
          <form onSubmit={verify} className="public-form">
            <label className="visually-hidden" htmlFor="demo-code">Six-digit code</label>
            <input id="demo-code" className="form-control public-code" required
                   inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                   placeholder="000000" autoFocus
                   value={code} disabled={busy}
                   onChange={e => setCode(e.target.value)} />
            <button className="btn btn-primary" type="submit" disabled={busy || !code.trim()}>
              {busy ? 'Checking…' : 'Enter the demo'}
            </button>
          </form>

          <p className="public-alt">
            <button type="button" className="btn btn-link p-0"
                    onClick={() => { setStage('email'); setCode(''); setError(null); }}>
              Use a different email, or send another code
            </button>
          </p>
        </>
      )}
    </PublicShell>
  );
}
